import { Injectable, Type } from '@angular/core';
import { BaseApiService } from '@kinet-open-network/client/core/api-client';
import { mapToModel } from '@kinet-open-network/client/shared/data-access/model';
import {
  downloadBlob,
  getFilenameFromHeaders,
} from '@kinet-open-network/client/shared/util';
import { IDocumentTemplate } from '@kinet-open-network/shared/data-access/model';
import { firstValueFrom, Observable } from 'rxjs';
import {
  CreateDocumentPayload,
  Document,
  SendDocumentPayload,
  UploadDocumentPayload,
} from '../models/document.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService extends BaseApiService<Document> {
  override model: Type<Document> = Document;

  override get endpoint(): string {
    return '/documents';
  }

  createDocument(data: CreateDocumentPayload) {
    return super.create(data as Partial<Document>);
  }

  /** Templates offered for delivery documents (flowTypes includes 'deliveries'). */
  getTemplatesOptions(contextType: string): Observable<IDocumentTemplate[]> {
    return this.http.get<IDocumentTemplate[]>(
      `${this.endpoint}/templates/options/${contextType}`,
    );
  }

  getTemplate(id: string): Observable<IDocumentTemplate> {
    return this.http.get<IDocumentTemplate>(
      `/document-templates/${encodeURIComponent(id)}`,
    );
  }

  getPdf(id: string) {
    return firstValueFrom(
      this.http.get(`${this.endpoint}/${this.encodeId(id)}/download`, {
        responseType: 'blob',
        observe: 'response',
      }),
    );
  }

  downloadPdf(id: string, name?: string) {
    return this.getPdf(id).then((res) => {
      if (res.body) {
        const filename = getFilenameFromHeaders(res.headers);
        return downloadBlob(res.body, filename || `${name || id}.pdf`);
      }
    });
  }

  /** Opens the PDF in a new browser tab. */
  openPdf(id: string) {
    return this.getPdf(id).then((res) => {
      if (!res.body) return;
      const blob = new Blob([res.body], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Delay revoke so the new tab has time to load the blob
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    });
  }

  sign(id: string, signature: string) {
    return firstValueFrom(
      this.http.post<Document>(`${this.endpoint}/${this.encodeId(id)}/sign`, {
        signature,
      }),
    );
  }

  print(id: string, name?: string) {
    return this.getPdf(id).then((file) => {
      const blob = file.body as Blob;
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    });
  }

  signDocument(id: string, signature: string) {
    return this.http
      .post<Document>(`${this.endpoint}/${id}/sign`, { signature })
      .pipe(mapToModel(this.model));
  }

  sendDocument(id: string, data: SendDocumentPayload) {
    return this.http.post(`/dispatch/documents/${id}/send`, data);
  }

  generate(id: string) {
    return this.http.post(`${this.endpoint}/${id}/generate`, {});
  }

  upload(data: UploadDocumentPayload): Observable<Document> {
    return this.http.post<Document>(`${this.endpoint}/upload`, data);
  }
}
